/**
 * Signalbox client types and API calls.
 *
 * The board arrives from the server with every DUE DATE already resolved,
 * because working-day arithmetic depends on the England & Wales bank holiday
 * list and that has to come from gov.uk server-side. What the client derives is
 * STATUS — which must be client-side so a row can turn overdue while the page is
 * open, without a refetch and without a nightly job.
 *
 * Dates cross the wire as 'YYYY-MM-DD' and are parsed to local midnight by
 * `day()`. Never `new Date('2026-08-31')` directly: that parses as UTC midnight,
 * which in British Summer Time is the previous day at 01:00 and shifts every
 * date by one.
 */
import { apiFetch } from '../api'

export type Cadence = 'monthly' | 'quarterly' | 'annual'
export const CADENCES: Array<[Cadence, string]> = [
  ['monthly', 'Monthly'],
  ['quarterly', 'Quarterly'],
  ['annual', 'Annual'],
]
export const CADENCE_LABEL: Record<Cadence, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annually',
}

export interface Person {
  upn: string
  displayName: string | null
  role: 'admin' | 'reader' | 'none'
}

export interface NoteOut {
  by: string
  at: string
  text: string
}

export interface PeriodOut {
  id: string
  label: string
  long: string
  end: string
  openFrom: string
  open: boolean
  openedEarly: boolean
  locked: boolean
  completions: number
}

export interface OccurrenceOut {
  periodId: string
  taskId: number
  due: string
  completedAt: string | null
  completedBy: string | null
  notes: NoteOut[]
}

export type Lifecycle = 'open' | 'paused' | 'closed'
export type ReminderKind = 'on_day' | 'day_before' | 'week_before' | 'digest'

/**
 * When the owner is emailed. Chosen per task because the right warning differs
 * by the work — a bank reconciliation needs a nudge on the day, the statutory
 * accounts need a week.
 */
export const REMINDERS: Array<[ReminderKind | '', string, string]> = [
  ['', 'No reminder', 'Nobody is emailed about this task'],
  ['on_day', 'On the day', 'The morning it is due'],
  ['day_before', 'The day before', 'One day ahead'],
  ['week_before', 'A week before', 'Seven days ahead'],
  ['digest', 'Daily digest', 'Rolled into one summary email a day rather than its own'],
]

export const LIFECYCLES: Array<[Lifecycle, string, string]> = [
  ['open', 'Open', 'Runs every period as normal'],
  ['paused', 'Paused', 'Stops generating new work but keeps everything already signed off'],
  ['closed', 'Closed', 'Ceases from the next open period. History is kept'],
]

export interface DefinitionOut {
  id: number
  cadence: Cadence
  name: string
  assigneeUpn: string | null
  due: number
  dueType: 'working' | 'calendar'
  effectiveFrom: string
  ceasedFrom: string | null
  ref: string | null
  instructions: string | null
  taskGroup: string | null
  lifecycle: Lifecycle
  reminder: ReminderKind | null
}

export interface AttachmentOut {
  id: number
  taskId: number | null
  programmeTaskId: number | null
  fileName: string
  sizeBytes: number
  uploadedBy: string
  uploadedUtc: string
}

export interface MilestoneOut {
  programmeTaskId: number
  name: string
  assigneeUpn: string | null
  ref: string | null
  instructions: string | null
  planned: string
  due: string
  overridden: boolean
  completedAt: string | null
  completedBy: string | null
  notes: NoteOut[]
}

export interface CycleOut {
  fy: number
  periodId: string
  label: string
  runsIn: string
  start: string
  open: boolean
  openedEarly: boolean
  overrides: number
  milestones: MilestoneOut[]
}

export interface ProgrammeOut {
  id: number
  name: string
  blurb: string | null
  repeatKind: 'annual' | 'oneoff'
  naming: 'next' | 'runs'
  startDay: number
  startMonth: number
  firstFy: number
  tasks: Array<{
    id: number
    name: string
    assigneeUpn: string | null
    day: number
    month: number
    ref: string | null
    instructions: string | null
  }>
  finished: boolean
  cycles: CycleOut[]
}

export interface Board {
  today: string
  liveFrom: string
  fys: number[]
  me: { upn: string; displayName: string | null; isAdmin: boolean }
  people: Person[]
  bankHolidays: { through: string | null; fetchedUtc: string | null }
  periods: Record<Cadence, PeriodOut[]>
  occurrences: Record<Cadence, OccurrenceOut[]>
  definitions: DefinitionOut[]
  attachments: AttachmentOut[]
  programmes: ProgrammeOut[]
}

export const getBoard = () => apiFetch<Board>('/api/signalbox')

interface OccBody {
  periodId: string
  taskId?: number
  programmeTaskId?: number
}

const post = <T = { ok: true }>(path: string, body?: unknown) =>
  apiFetch<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

export const complete = (ref: OccBody) => post('/api/signalbox/complete', ref)
export const reopen = (ref: OccBody) => post('/api/signalbox/reopen', ref)
export const addNote = (ref: OccBody & { text: string }) => post('/api/signalbox/notes', ref)

export const openPeriod = (periodId: string) => post('/api/signalbox/periods/open', { periodId })
export const forgetPeriod = (periodId: string) => post('/api/signalbox/periods/forget', { periodId })
export const reopenPeriod = (periodId: string) => post('/api/signalbox/periods/reopen', { periodId })

export interface TaskInput {
  name: string
  assigneeUpn: string | null
  due: number
  dueType: string
  ref: string | null
  instructions: string | null
  taskGroup: string | null
  reminder: string | null
}

export const createTask = (input: TaskInput & { cadence: Cadence; effectiveFrom: string }) =>
  post<{ ok: true; id: number }>('/api/signalbox/tasks', input)

export const updateTask = (id: number, input: TaskInput) =>
  apiFetch<{ ok: true }>(`/api/signalbox/tasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })

export const deleteTask = (id: number) =>
  apiFetch<{ ok: true }>(`/api/signalbox/tasks/${id}`, { method: 'DELETE' })

export const ceaseTask = (id: number, from: string) =>
  post(`/api/signalbox/tasks/${id}/cease`, { from })

/** open / paused / closed. The server works out what to cease from. */
export const setTaskStatus = (id: number, lifecycle: Lifecycle) =>
  post<{ ok: true; lifecycle: Lifecycle; ceasedFrom: string | null }>(
    `/api/signalbox/tasks/${id}/status`,
    { lifecycle }
  )

/** Multipart — no Content-Type header, the browser sets the boundary. */
export async function uploadAttachment(taskId: number, file: File): Promise<void> {
  const form = new FormData()
  form.append('file', file)
  await apiFetch<{ ok: true }>(`/api/signalbox/tasks/${taskId}/attachments`, {
    method: 'POST',
    body: form,
  })
}

export const deleteAttachment = (id: number) =>
  apiFetch<{ ok: true }>(`/api/signalbox/attachments/${id}`, { method: 'DELETE' })

export interface ProgrammeInput {
  name: string
  blurb: string | null
  repeatKind: string
  naming: string
  startDay: number
  startMonth: number
  firstFy: number
}

export const createProgramme = (input: ProgrammeInput) =>
  post<{ ok: true; id: number }>('/api/signalbox/programmes', input)

export const updateProgramme = (id: number, input: ProgrammeInput) =>
  apiFetch<{ ok: true }>(`/api/signalbox/programmes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })

export const deleteProgramme = (id: number) =>
  apiFetch<{ ok: true }>(`/api/signalbox/programmes/${id}`, { method: 'DELETE' })

export interface MilestoneInput {
  name: string
  assigneeUpn: string | null
  day: number
  month: number
  ref: string | null
  instructions: string | null
}

export const createMilestone = (programmeId: number, input: MilestoneInput) =>
  post<{ ok: true; id: number }>(`/api/signalbox/programmes/${programmeId}/tasks`, input)

export const updateMilestone = (programmeId: number, taskId: number, input: MilestoneInput) =>
  apiFetch<{ ok: true }>(`/api/signalbox/programmes/${programmeId}/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  })

export const deleteMilestone = (programmeId: number, taskId: number) =>
  apiFetch<{ ok: true }>(`/api/signalbox/programmes/${programmeId}/tasks/${taskId}`, {
    method: 'DELETE',
  })

export const openCycle = (programmeId: number, fy: number) =>
  post(`/api/signalbox/programmes/${programmeId}/cycles/${fy}/open`)

export const forgetCycle = (programmeId: number, fy: number) =>
  apiFetch<{ ok: true }>(`/api/signalbox/programmes/${programmeId}/cycles/${fy}/open`, {
    method: 'DELETE',
  })

export const setCycleDates = (programmeId: number, fy: number, dates: Record<number, string>) =>
  apiFetch<{ ok: true }>(`/api/signalbox/programmes/${programmeId}/cycles/${fy}/dates`, {
    method: 'PUT',
    body: JSON.stringify({ dates }),
  })
